/**
 * The controls above a scope dropdown's list: search, tracker, overridden-only.
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC, type RefObject } from 'react';
import { Filter, Search } from 'lucide-react';
import type { TrackerId } from '@trafficpulse/shared';

import { ProviderFilter, type ProviderFilterValue } from '@/components/ui';
import { useEnabledTrackers } from '@/features/trackers/useEnabledTrackers';
import { cn } from '@/lib/utils';

type TrackerFilterValue = ProviderFilterValue<TrackerId>;

/** All + the trackers this deployment reads: Tres Commas only where it is enabled. */
const TrackerFilter: FC<{ value: TrackerFilterValue; onChange: (v: TrackerFilterValue) => void }> = ({
  value,
  onChange,
}) => {
  const trackers = useEnabledTrackers();
  return <ProviderFilter value={value} onChange={onChange} providers={trackers} label="Tracker" />;
};

export const ScopeComboboxHeader: FC<{
  label: string;
  term: string;
  onTerm: (v: string) => void;
  inputRef: RefObject<HTMLInputElement>;
  /** How many entries carry an override at all — drives the toggle's count. */
  overriddenTotal: number;
  onlyOverridden: boolean;
  onOnlyOverridden: (v: boolean) => void;
  showProviderFilter?: boolean;
  provider: TrackerFilterValue;
  onProvider: (v: TrackerFilterValue) => void;
}> = ({
  label,
  term,
  onTerm,
  inputRef,
  overriddenTotal,
  onlyOverridden,
  onOnlyOverridden,
  showProviderFilter,
  provider,
  onProvider,
}) => (
  <>
    <div className="flex items-center gap-2 border-b border-border/60 px-2 py-1.5">
      <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <input
        ref={inputRef}
        value={term}
        onChange={(e) => onTerm(e.target.value)}
        placeholder="Search by name or id…"
        aria-label={`Search ${label}`}
        className="h-6 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
      />
    </div>
    <div className="flex flex-wrap items-center gap-2 border-b border-border/60 px-2 py-1.5">
      {showProviderFilter ? (
        <TrackerFilter value={provider} onChange={onProvider} />
      ) : null}
      {/* Disabled at zero rather than hidden: "none of these are overridden" is
          itself the answer, and a button that vanishes is one you stop trusting. */}
      <button
        type="button"
        onClick={() => onOnlyOverridden(!onlyOverridden)}
        aria-pressed={onlyOverridden}
        disabled={overriddenTotal === 0}
        title={
          overriddenTotal === 0
            ? 'Nothing here has an override yet'
            : 'Show only entries that already have a value set'
        }
        className={cn(
          'ml-auto inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase leading-none tracking-wide transition-colors',
          'disabled:cursor-not-allowed disabled:opacity-50',
          onlyOverridden
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border text-muted-foreground hover:text-foreground',
        )}
      >
        <Filter className="h-3 w-3" aria-hidden />
        With overrides
        <span className="tabular-nums opacity-80">{overriddenTotal}</span>
      </button>
    </div>
  </>
);
